import { Modal, TextInput, SegmentedControl, Select, Button, Stack, Text } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { CreatePlaylistSchema, type CreatePlaylistFormValues } from '../schemas/PlaylistForm.schema';
import { useCreateCustomPlaylist, useCreateTagPlaylist } from '../usePlaylistMutations';
import { PLAYLIST_CONSTRAINTS } from '@/lib/constants/playlist';
import type { CreateCustomPlaylistRequest, CreateTagPlaylistRequest } from '@/lib/types/Playlist';
import { MediaStateType } from '@lib2/model/media/MediaTypes';
import { MediaType } from '@lib2/model/media/MediaType';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface CreatePlaylistModalProps {
  opened: boolean;
  onClose: () => void;
}

export function CreatePlaylistModal({ opened, onClose }: CreatePlaylistModalProps) {
  const navigate = useNavigate();
  const { selectedCId } = useAuth();
  const createCustom = useCreateCustomPlaylist();
  const createTag = useCreateTagPlaylist();

  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<CreatePlaylistFormValues>({
    resolver: zodResolver(CreatePlaylistSchema),
    defaultValues: {
      title: '',
      listType: 'tag',
      mediaType: 'video',
    },
  });

  const titleField = useController({ name: 'title', control });
  const listTypeField = useController({ name: 'listType', control });
  const mediaTypeField = useController({ name: 'mediaType', control });

  const onSubmit = async (data: CreatePlaylistFormValues) => {
    if (!selectedCId) return;

    try {
      const mediaState = MediaStateType.Active;
      const mediaType = data.mediaType as MediaType;

      if (data.listType === 'custom') {
        const req: CreateCustomPlaylistRequest = {
          mediaState,
          mediaType,
          cId: selectedCId,
          title: data.title,
        };
        const result = await createCustom.mutateAsync(req);
        const displayId = bcLong.getStr(result.listId);
        handleClose();
        navigate({ to: '/playlists/$id', params: { id: displayId } });
      } else {
        const req: CreateTagPlaylistRequest = {
          mediaState,
          mediaType,
          cId: selectedCId,
          title: data.title,
          tagRules: [{ tag: '', rule: 'or' }],
          sortType: 'published',
          sortDirection: 'desc',
          limit: null,
        };
        const result = await createTag.mutateAsync(req);
        const displayId = bcLong.getStr(result.listId);
        handleClose();
        navigate({ to: '/playlists/$id', params: { id: displayId } });
      }

      notifications.show({
        title: 'Created',
        message: `Playlist "${data.title}" created successfully.`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create playlist',
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create Playlist" size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <div>
            <Text size="sm" fw={500} mb={4}>Playlist Type</Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: 'Smart Playlist', value: 'tag' },
                { label: 'Manual Playlist', value: 'custom' },
              ]}
              value={listTypeField.field.value}
              onChange={listTypeField.field.onChange}
            />
          </div>

          <Select
            label="Media Type"
            data={[
              { value: 'video', label: 'Video' },
              { value: 'video_stream', label: 'Live Stream' },
            ]}
            value={mediaTypeField.field.value}
            onChange={(val) => val && mediaTypeField.field.onChange(val)}
            allowDeselect={false}
          />

          <TextInput
            label="Title"
            placeholder="Enter playlist title"
            required
            maxLength={PLAYLIST_CONSTRAINTS.TITLE_MAX}
            description={`${titleField.field.value.length}/${PLAYLIST_CONSTRAINTS.TITLE_MAX}`}
            value={titleField.field.value}
            onChange={titleField.field.onChange}
            onBlur={titleField.field.onBlur}
            error={titleField.fieldState.error?.message}
          />

          <Button type="submit" fullWidth loading={isSubmitting}>
            Create Playlist
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
